import { Button, IconButton, Tag, Tooltip } from '@ads/components-react';
import {
  ChevronRightIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { EClassRoomDisciplinesStatus } from '@/shared/api/generated';

import { useClassModuleForm } from '../../../context/ClassModuleFormContext';
import type { ClassModuleLessonView } from '../../../types/classModuleForm';

const HEADER_GRID = 'grid grid-cols-[2.4fr_1.1fr_1.6fr_1.6fr_5.75rem] items-center gap-micro';
const LESSON_GRID = 'grid grid-cols-[10.5rem_1.6fr_1.6fr_9rem_3.5rem] items-center gap-micro';

const STATUS_LABEL: Record<EClassRoomDisciplinesStatus, string> = {
  1: 'lessons.status.planned',
  2: 'lessons.status.finished',
  3: 'lessons.status.canceled',
  4: 'lessons.status.confirmed',
};

const STATUS_TONE: Record<
  EClassRoomDisciplinesStatus,
  'default' | 'positive' | 'negative' | 'helper'
> = {
  1: 'helper',
  2: 'positive',
  3: 'negative',
  4: 'positive',
};

function formatDate(value?: string): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—';
}

function formatTimeRange(lesson: ClassModuleLessonView): string {
  const start = lesson.startDate ? dayjs(lesson.startDate) : null;
  const end = lesson.endDate ? dayjs(lesson.endDate) : null;
  const startText = start?.isValid() ? start.format('HH:mm') : '';
  const endText = end?.isValid() ? end.format('HH:mm') : '';
  if (startText && endText) return `${startText} - ${endText}`;
  return startText || endText || '';
}

/**
 * Seção "Aulas e Disciplinas": tabela de disciplinas do módulo com linhas
 * expansíveis que revelam as aulas (somente leitura) geradas pelo backend.
 */
export function DisciplinesSection() {
  const { t } = useTranslation('classModuleManagement');
  const {
    disciplines,
    expandedDisciplineIndex,
    toggleDiscipline,
    openAddDiscipline,
    openEditDiscipline,
    requestRemoveDiscipline,
  } = useClassModuleForm();

  return (
    <div className="flex flex-col gap-mini">
      <div className="flex items-center justify-between border-ctx-layout-border border-b-hairline pb-nano">
        <h2 className="text-ctx-content-title ts-heading-xs">{t('disciplines.title')}</h2>
        <Button hierarchy="primary" size="sm" leadingIcon={PlusIcon} onClick={openAddDiscipline}>
          {t('disciplines.add')}
        </Button>
      </div>

      <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
        <div
          className={`${HEADER_GRID} bg-ctx-layout-body px-mini py-micro text-ctx-content-base ts-heading-xxxs`}
        >
          <span>{t('disciplines.columns.discipline')}</span>
          <span>{t('disciplines.columns.quantity')}</span>
          <span>{t('disciplines.columns.teacher')}</span>
          <span>{t('disciplines.columns.classroom')}</span>
          <span />
        </div>

        {disciplines.length === 0 ? (
          <div className="px-mini py-sm text-center">
            <p className="text-ctx-content-title ts-heading-xxs">{t('disciplines.empty.title')}</p>
            <p className="mt-quark text-ctx-content-placeholder ts-paragraph-xs">
              {t('disciplines.empty.hint')}
            </p>
          </div>
        ) : (
          disciplines.map((discipline, index) => {
            const expanded = expandedDisciplineIndex === index;
            return (
              <div
                key={`${discipline.disciplineId}-${index}`}
                className="border-ctx-layout-border border-t-hairline"
              >
                <div
                  className={`${HEADER_GRID} px-mini py-micro text-ctx-content-title ts-paragraph-xxs`}
                >
                  <Tooltip
                    title={discipline.disciplineName || t('disciplines.notDefined')}
                    side="top"
                  >
                    <button
                      type="button"
                      onClick={() => toggleDiscipline(index)}
                      className="flex items-center gap-xs text-left text-ctx-content-title ts-heading-xxs"
                    >
                      <ChevronRightIcon
                        className={`size-mini shrink-0 text-ctx-content-placeholder transition-transform ${expanded ? 'rotate-90' : ''}`}
                        aria-hidden="true"
                      />
                      <span className="line-clamp-2">
                        {discipline.disciplineName || t('disciplines.notDefined')}
                      </span>
                    </button>
                  </Tooltip>
                  <span>{discipline.quantity}</span>
                  <Tooltip
                    title={discipline.teacherName || t('disciplines.notDefined')}
                    side="top"
                    align="start"
                  >
                    <span className="line-clamp-2 text-ctx-content-base">
                      {discipline.teacherName || t('disciplines.notDefined')}
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={discipline.classroomName || t('disciplines.notDefined')}
                    side="top"
                    align="start"
                  >
                    <span className="line-clamp-2 text-ctx-content-base">
                      {discipline.classroomName || t('disciplines.notDefined')}
                    </span>
                  </Tooltip>
                  <span className="flex justify-end gap-quark">
                    <IconButton
                      icon={PencilSquareIcon}
                      hierarchy="tertiary"
                      size="sm"
                      ariaLabel={t('disciplines.edit')}
                      onClick={() => openEditDiscipline(index)}
                    />
                    <IconButton
                      icon={TrashIcon}
                      hierarchy="tertiary"
                      size="sm"
                      ariaLabel={t('disciplines.remove')}
                      onClick={() => requestRemoveDiscipline(index)}
                    />
                  </span>
                </div>

                {expanded && discipline.lessons.length === 0 && (
                  <div className="bg-ctx-layout-body px-mini py-mini border-t-hairline border-t-ctx-layout-border">
                    <p className="text-center text-ctx-content-placeholder ts-paragraph-xs">
                      {t('lessons.empty')}
                    </p>
                  </div>
                )}

                {expanded && discipline.lessons.length > 0 && (
                  <div className="bg-ctx-layout-body px-mini pb-mini">
                    <div
                      className={`${LESSON_GRID} px-micro py-nano text-ctx-content-base ts-heading-xxs`}
                    >
                      <span>{t('lessons.columns.date')}</span>
                      <span>{t('lessons.columns.teacher')}</span>
                      <span>{t('lessons.columns.classroom')}</span>
                      <span>{t('lessons.columns.status')}</span>
                      <span />
                    </div>
                    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
                      {discipline.lessons.map((lesson, lessonIndex) => (
                        <div
                          key={lesson.id ?? lessonIndex}
                          className={`${LESSON_GRID} border-ctx-layout-border border-b-hairline px-micro py-nano text-ctx-content-title ts-paragraph-xxs`}
                        >
                          <span>
                            <span className="block">{formatDate(lesson.startDate)}</span>
                            <span className="block text-ctx-content-placeholder ts-paragraph-xxs">
                              {formatTimeRange(lesson)}
                            </span>
                          </span>
                          <span className="text-ctx-content-base">
                            {discipline.teacherName || t('disciplines.notDefined')}
                          </span>
                          <span className="text-ctx-content-base">
                            {discipline.classroomName || t('disciplines.notDefined')}
                          </span>
                          <span>
                            {lesson.status ? (
                              <Tag
                                size="sm"
                                status={STATUS_TONE[lesson.status]}
                                label={t(STATUS_LABEL[lesson.status] as 'lessons.status.planned')}
                                custom={{ text: 'text-nowrap' }}
                              />
                            ) : (
                              <span className="text-ctx-content-placeholder">
                                {t('lessons.status.unknown')}
                              </span>
                            )}
                          </span>
                          <span />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
